/**
 * Mini-rendu markdown SÛR, sans dépendance : titres (#, ##, ###), gras (**…**),
 * listes à puces (-, *) et numérotées (1.), paragraphes.
 *
 * Le résultat est un ARBRE (blocs → segments de texte), jamais du HTML : c'est
 * le composant React qui le rend, et React échappe tout texte. Un `<script>`
 * dans un contrat s'affiche donc tel quel, en clair — aucune injection
 * possible, et aucun besoin de `dangerouslySetInnerHTML`.
 */
export interface Segment {
  texte: string
  gras: boolean
}
export type BlocMd =
  | { type: 'titre'; niveau: 1 | 2 | 3; segments: Segment[] }
  | { type: 'paragraphe'; segments: Segment[] }
  | { type: 'liste'; ordonnee: boolean; elements: Segment[][] }

/** `**gras**` → segments. Un `**` non refermé reste du texte brut. */
export function segmentsInline(ligne: string): Segment[] {
  const morceaux = ligne.split('**')
  // Nombre pair de morceaux = un `**` orphelin : on le rend littéralement.
  if (morceaux.length % 2 === 0) return [{ texte: ligne, gras: false }]
  return morceaux
    .map((texte, i) => ({ texte, gras: i % 2 === 1 }))
    .filter((s) => s.texte !== '')
}

export function analyserMarkdown(md: string): BlocMd[] {
  const blocs: BlocMd[] = []
  let paragraphe: string[] = []
  let liste: { ordonnee: boolean; elements: Segment[][] } | null = null

  const fermerParagraphe = () => {
    if (paragraphe.length) blocs.push({ type: 'paragraphe', segments: segmentsInline(paragraphe.join(' ')) })
    paragraphe = []
  }
  const fermerListe = () => {
    if (liste) blocs.push({ type: 'liste', ordonnee: liste.ordonnee, elements: liste.elements })
    liste = null
  }

  for (const brute of md.replace(/\r\n?/g, '\n').split('\n')) {
    const ligne = brute.trim()
    if (ligne === '') {
      fermerParagraphe()
      fermerListe()
      continue
    }
    const titre = /^(#{1,3})\s+(.*)$/.exec(ligne)
    if (titre) {
      fermerParagraphe()
      fermerListe()
      blocs.push({
        type: 'titre',
        niveau: (titre[1] ?? '#').length as 1 | 2 | 3,
        segments: segmentsInline(titre[2] ?? ''),
      })
      continue
    }
    const puce = /^[-*]\s+(.*)$/.exec(ligne)
    const numero = /^\d+[.)]\s+(.*)$/.exec(ligne)
    const item = puce ?? numero
    if (item) {
      fermerParagraphe()
      const ordonnee = Boolean(numero) && !puce
      if (liste && liste.ordonnee !== ordonnee) fermerListe()
      liste ??= { ordonnee, elements: [] }
      liste.elements.push(segmentsInline(item[1] ?? ''))
      continue
    }
    fermerListe()
    paragraphe.push(ligne)
  }
  fermerParagraphe()
  fermerListe()
  return blocs
}
